/**
 * An iPhone photo, made into one the server can read.
 *
 * iPhones save photographs as HEIC (a HEIF file holding an HEVC picture). The
 * server takes the type but its image library cannot decode HEVC, so such a
 * photo was refused as unreadable. Here it is turned into a JPEG in the
 * browser before it goes anywhere, and every other picture is left alone.
 *
 * A HEIC is recognised by its type where the browser gives one (image/heic,
 * image/heif and their -sequence forms). Where the type is empty or says only
 * "some bytes" — Windows and the Files app often do — the name (.heic, .heif)
 * or the file's first bytes (an ISO box naming a HEIF brand) say it instead.
 *
 * The converter (heic-to, libheif compiled for the browser) is a few megabytes
 * and only an iPhone photo needs it, so it is fetched the first time one is
 * chosen rather than with the app. It decodes in a worker of its own, so the
 * page stays responsive meanwhile.
 *
 * The JPEG carries no EXIF. Whoever wants the photo's date reads it from the
 * original file, before or after converting: PhotoProcessor does.
 *
 * Who uses it: services/PhotoProcessor (every photo the app uploads goes
 * through it), the photo dialog's preview (UploadPhotoDialog) and the
 * tray's uploader (TrayUploader).
 *
 * @module uploadablePhoto
 */

import { APP_CONSTANTS } from '@/config/config';
import { t } from '@/shared/i18n/i18nSetup';

/** The types a browser gives a HEIC or HEIF file, still pictures and bursts alike. */
const HEIF_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);

/** Types that say nothing about what the file is, so the name and bytes are asked instead. */
const GENERIC_TYPES = new Set(['', 'application/octet-stream', 'binary/octet-stream']);

/** A HEIC or HEIF file's name, whatever its case. */
const HEIF_NAME = /\.(heic|heif)$/i;

/**
 * The brands in a file's opening `ftyp` box that mean a HEIF still or
 * sequence: HEVC-coded (heic, heix, hevc, hevx and their multi-layer forms)
 * and the generic HEIF brands (mif1, msf1).
 */
const HEIF_BRANDS = new Set(['heic', 'heix', 'heim', 'heis', 'hevc', 'hevx', 'hevm', 'hevs', 'mif1', 'msf1']);

/**
 * Brands that make a generic HEIF file an AVIF instead. An AVIF can open with
 * mif1 too; it is not an iPhone photo and heic-to would not read it.
 */
const AVIF_BRANDS = new Set(['avif', 'avis']);

/** How much of the file is read to find its brands: the ftyp box is short. */
const SNIFF_BYTES = 64;

/**
 * Whether the file's opening bytes are a HEIF `ftyp` box.
 *
 * An ISO media file opens with a box: four bytes of length, then "ftyp", then
 * the major brand and a minor version, then the brands it is also compatible
 * with. A HEIF brand anywhere among them, and no AVIF one, is a HEIC.
 *
 * @param file - The chosen file.
 * @returns True for a HEIF picture; false for anything else, or a file that cannot be read.
 */
async function hasHeifBrand(file: Blob): Promise<boolean> {
  let bytes: Uint8Array;
  try {
    bytes = new Uint8Array(await file.slice(0, SNIFF_BYTES).arrayBuffer());
  } catch {
    return false;
  }
  if (bytes.length < 12) return false;
  /** Four bytes from `at`, as text. */
  const word = (at: number): string => String.fromCharCode(bytes[at], bytes[at + 1], bytes[at + 2], bytes[at + 3]);
  if (word(4) !== 'ftyp') return false;

  // The box's own length bounds the brand list; a nonsense length is held to what was read.
  const declared = ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0;
  const end = Math.min(declared >= 16 ? declared : bytes.length, bytes.length);
  const brands = [word(8)];
  for (let at = 16; at + 4 <= end; at += 4) brands.push(word(at));

  if (brands.some((brand) => AVIF_BRANDS.has(brand))) return false;
  return brands.some((brand) => HEIF_BRANDS.has(brand));
}

/**
 * Whether the file is a HEIC or HEIF picture, by its type, or, where the type
 * says nothing, by its name or its first bytes.
 *
 * A file with a real type of its own (image/jpeg, say) is taken at its word:
 * Google Photos hands over a JPEG still named IMG_0001.HEIC.
 *
 * @param file - The chosen file.
 * @returns True when it has to be converted before it is sent.
 */
export async function isHeic(file: File): Promise<boolean> {
  const type = file.type.toLowerCase();
  if (HEIF_TYPES.has(type)) return true;
  if (!GENERIC_TYPES.has(type)) return false;
  if (HEIF_NAME.test(file.name)) return true;
  return hasHeifBrand(file);
}

/**
 * The name the JPEG goes by: the original's, with .jpg in place of its
 * extension (IMG_0001.HEIC becomes IMG_0001.jpg), or a plain name when it had
 * none worth keeping.
 *
 * @param name - The original's name.
 * @returns The JPEG's name.
 */
export function jpegName(name: string): string {
  const base = name.replace(/\.[^./\\]*$/, '');
  return `${base || 'photo'}.jpg`;
}

/**
 * The file to upload: the same file, unless it is a HEIC or HEIF, which comes
 * back as a JPEG with the same name ending .jpg and the same lastModified.
 *
 * @param file - The picture as the gardener chose it.
 * @returns A file the server can read.
 * @throws Error whose message is a sentence for the gardener, in their
 *   language, when an iPhone photo could not be converted.
 */
export async function toUploadable(file: File): Promise<File> {
  if (!(await isHeic(file))) return file;
  try {
    // Fetched only now, as its own chunk: a few megabytes nobody else needs.
    const { heicTo } = await import('heic-to');
    const jpeg = await heicTo({ blob: file, type: 'image/jpeg', quality: APP_CONSTANTS.PHOTO.QUALITY });
    return new File([jpeg], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
  } catch (cause) {
    // The converter rejects with a bare string at times; kept for whoever debugs it.
    const error = new Error(t('plants.photo.heicFailed'));
    (error as Error & { cause?: unknown }).cause = cause;
    throw error;
  }
}
